/**
 * @module reporting filters
 *
 * The selections the user has made to indicate their intention to view data.
 * These include both selected surveys and attributes as well as comparisons.
 *
 * @example
 * selectedAttributeValues: {
 *   80: ['value1', 'value2', 'value3'],
 *   123: ['value4', 'value5']
 * }
 */
import { createReducer } from '@reduxjs/toolkit';
import { surveyReportingActions } from '~/behaviors/actions/survey_reporting.actions';
import { reportingActions } from '~/behaviors/actions/reporting.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';

const defaultState = {
  // reporting selections
  selectedSurveys: [],
  selectedAttributeValues: {},
  isHierarchy: false,

  // comparisons
  comparedSurvey: null,
  comparedAttributes: {},
  isComparedOrgWide: false,
  compareBenchmarks: {},

  timeSliceSearch: {},
  compareTimeSlice: {},

  selectedParticipant: null,
  selectedTimeframes: [],
};

const reset = () => defaultState;

const resetReportingState = (state, action) => {
  const surveyIds = action?.payload?.surveyIds || [];
  state.selectedSurveys = surveyIds;
  state.selectedAttributeValues = {};
  state.isHierarchy = false;
};

const resetReportingSelections = (state) => {
  state.selectedAttributeValues = {};
  state.isHierarchy = false;
};

const resetCompared = (state) => {
  state.comparedSurvey = null;
  state.comparedAttributes = {};
  state.isComparedOrgWide = false;
  state.compareBenchmarks = {};
  state.compareTimeSlice = {};
};

const resetParticipant = (state) => {
  state.selectedParticipant = null;
};

// TODO: add some validation (e.g., ids actually exist and have data)
const setReportingSurveys = (state, action) => {
  resetCompared(state);
  state.selectedSurveys = action?.payload?.surveys || [];
};

// This is setup to be a dictionary of attributeIds containing an array of
// attributes + their values + calculated meta data.
const setReportingAttributeValues = (state, action) => {
  state.selectedAttributeValues = action.payload.attributeValues || {};
};

const setReportingHierarchy = (state, action) => {
  state.isHierarchy = action.payload.isHierarchy;
};

const removeReportingAttributeValues = (state, action) => {
  resetCompared(state);

  const { attributeIds } = action.payload;

  attributeIds.forEach((attributeId) => {
    delete state.selectedAttributeValues[attributeId];
  });
};

// TODO: add some validation (e.g., ids actually exist and have data)
const setComparedSurvey = (state, action) => {
  resetCompared(state);
  state.comparedSurvey = action.payload.surveyId || null;
};

// TODO: add some validation (e.g., ids actually exist and have data)
const setComparedAttributes = (state, action) => {
  resetCompared(state);
  state.comparedAttributes = action.payload.attributes || {};
};

const setComparedOrgWide = (state, action) => {
  resetCompared(state);
  state.isComparedOrgWide = action.payload.isComparedOrgWide;
};

const setComparedBenchmarks = (state, action) => {
  const currentSetting = state.compareBenchmarks;
  resetCompared(state);
  state.compareBenchmarks = { ...currentSetting, ...action.payload };
};

const setTimeSliceSearch = (state, action) => {
  state.timeSliceSearch = {
    startTime: action.payload.startTime,
    endTime: action.payload.endTime,
    timeframe: action.payload.timeframe,
  };
  // Reset the "Compare Time Slice" filter every time the "Time Slice" filter changes
  state.compareTimeSlice = {};
};

const setCompareTimeSlice = (state, action) => {
  resetCompared(state);
  state.compareTimeSlice = {
    startTime: action.payload.startTime,
    endTime: action.payload.endTime,
    timeframe: action.payload.timeframe,
  };
};

const setSelectedParticipant = (state, action) => {
  state.selectedParticipant = action.payload.participant || null;
};

const setSelectedTimeframes = (state, action) => {
  state.selectedTimeframes = action.payload.selectedTimeframes || [];
};

const filters = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(reportingActions.resetReporting, reset)
    .addCase(surveyReportingActions.resetReportingState, resetReportingState)
    .addCase(reportingActions.resetReportingSelections, resetReportingSelections)
    .addCase(reportingActions.resetCompared, resetCompared)
    .addCase(reportingActions.setReportingSurveys, setReportingSurveys)
    .addCase(reportingActions.setReportingAttributeValues, setReportingAttributeValues)
    .addCase(reportingActions.setReportingHierarchy, setReportingHierarchy)
    .addCase(reportingActions.removeReportingAttributeValues, removeReportingAttributeValues)
    .addCase(reportingActions.setComparedSurvey, setComparedSurvey)
    .addCase(reportingActions.setComparedAttributes, setComparedAttributes)
    .addCase(reportingActions.setComparedOrgWide, setComparedOrgWide)
    .addCase(reportingActions.setCompareBenchmarks, setComparedBenchmarks)
    .addCase(reportingActions.setTimeSliceSearch, setTimeSliceSearch)
    .addCase(reportingActions.setCompareTimeSlice, setCompareTimeSlice)
    .addCase(reportingActions.setSelectedParticipant, setSelectedParticipant)
    .addCase(reportingActions.resetParticipant, resetParticipant)
    .addCase(reportingActions.setTrendsSelectedTimeframes, setSelectedTimeframes);
});

export default filters;
